import {
	Component,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	ChangeDetectorRef,
	HostListener,
	AfterViewInit,
	Inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

// ** rxjs
import { finalize, switchMap, tap } from 'rxjs/operators';
import { of, Observable, forkJoin } from 'rxjs';

// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { NewsService } from '@app/core/services/news.service';

//  ** env
import { environment } from '@env/environment';

import { SwiperComponent } from 'swiper/angular';

@Component({
	templateUrl: './news.component.html',
	styleUrls: ['./news.component.scss'],
})
export class NewsComponent implements OnInit, AfterViewInit {
	@ViewChild(SwiperComponent, { static: false }) swiper: SwiperComponent;
	isBrowser: boolean;

	// ** news / electronic
	type = null;
	// ** file title from url
	fileTitle = null;
	// ** 全文章內容
	fullPiece;
	// ** 全部文章的總覽
	allNewsSheet;
	// ** 相關 topics arr
	relatedPieces = [];
	// ** 目前閱讀的文章 tags arr
	relation = [];

	// ** swiper current 頁數 reference
	swiperPageRef = 1;
	// ** swiper 顯示的數量
	perPage = 3;

	showSwiper = false;

	@HostListener('window:resize', ['$event'])
	onResize(event: Event): void {
		if (this.isBrowser) this.onWindowResize();
	}

	// - 依據 window size 調整 swiper 顯示 slide 數量
	onWindowResize() {
		const lg = window.innerWidth > 1024;
		const md = window.innerWidth > 440 && window.innerWidth <= 1024;
		const sm = window.innerWidth <= 440;
		this.perPage = lg ? 3 : md ? 2 : sm ? 1 : 0;
		this.updateScroll();
	}

	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId,
		private route: ActivatedRoute,
		private title: Title,
		private router: Router,
		private newsService: NewsService,
		private layoutService: LayoutService,
		private cdr: ChangeDetectorRef,
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
		if (this.isBrowser) this.onWindowResize();
	}

	ngOnInit(): void {
		this.initPage();
	}

	ngAfterViewInit(): void {}

	initPage() {
		this.route.params
			.pipe(
				tap(() => this.layoutService.setPageLoading(true)),
				tap(params => {
					const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLAnchorElement | null;
					if (canonical)
						canonical.href = `https://www.kunyoutech.com.tw/newsCenter/${params['type']}/${params['title']}`;
				}),
				tap(params => ([this.type, this.fileTitle] = [params['type'], params['title']])),
				switchMap(() => this.getFullPiece$(this.type, `${this.fileTitle}.json`)),
				tap(res => (this.fullPiece = res)),
				tap(() => (this.relation = [...this.fullPiece.Tags])),
				tap(() => this.getAllNews()),
				tap(res => this.setTitle(res?.Content)),
				finalize(() => this.layoutService.setPageLoading(false)),
			)
			.subscribe();
	}

	// - 初始化 swiper
	initSwiper() {
		setTimeout(() => (this.showSwiper = true));
		setTimeout(() => this.swiper?.swiperRef?.on('slideChange', e => this.updatePageIdx()));
	}

	// - 設定 title for seo
	setTitle(title) {
		if (title) this.title.setTitle(`${environment.projectTitleCH} - ${title}`);
	}

	// - 更新頁數
	updatePageIdx() {
		setTimeout(() => {
			this.swiperPageRef = this.swiper?.swiperRef ? this.swiper?.swiperRef.activeIndex + 1 : 1;
			this.cdr.detectChanges();
		});
	}

	// - manual update swiper scroll for ng lifecycle hotfix
	updateScroll() {
		setTimeout(() => {
			this.swiper?.swiperRef?.scrollbar.updateSize();
			this.swiper?.swiperRef?.scrollbar.setTranslate();
		});
	}

	// - 檢視文章
	goViewNews(prefix, fileTitle) {
		this.resetDataAndView();
		of(this.layoutService.setPageLoading(true))
			.pipe(
				tap(() => setTimeout(() => this.router.navigateByUrl(`newsCenter/${prefix}/${fileTitle}`))),
				finalize(() => this.layoutService.setPageLoading(false)),
			)
			.subscribe();
	}

	// - 取得所有文章的 sheet，排序時間
	getAllNews() {
		of(this.layoutService.setPageLoading(true))
			.pipe(
				switchMap(() => forkJoin([this.getNews$(), this.getElectronicNews$()])),
				tap(([news, eNews]) => {
					// 因為電子報未提供 tags，無法抓相關議題
					this.allNewsSheet = [...news];
					this.allNewsSheet.sort((a, b) => {
						const dateA = new Date(a.Date);
						const dateB = new Date(b.Date);
						return dateB.getTime() - dateA.getTime();
					});
				}),
				tap(() => this.findRelated()),
				finalize(() => this.layoutService.setPageLoading(false)),
			)
			.subscribe();
	}

	// - 找到相關主題
	findRelated() {
		if (!this.relation.length) this.preventNoRelatedNews();
		this.allNewsSheet.some((piece, idx) => {
			if (this.relatedPieces.length === 9) return true;
			// ** 找相關 tag
			const isRelated = piece.Tags.some(x =>
				this.relation.some(y => new RegExp(y, 'i').test(x) || new RegExp(x, 'i').test(y)),
			);
			// ** filter 自己
			const isSelf = piece.TypeEN === this.type && piece.FileTitle === this.fullPiece.FileTitle;
			if (isRelated && !isSelf) this.relatedPieces = [...this.relatedPieces, piece];
		});
		if (!this.relatedPieces.length) this.preventNoRelatedNews();

		this.initSwiper();
	}

	// - 避免相關主題全部都沒有
	preventNoRelatedNews() {
		this.relatedPieces = [...this.allNewsSheet.slice(0, 9)].filter(x => x.FileTitle !== this.fullPiece.FileTitle);
	}

	// - 重置相關主題 data & swiper
	resetDataAndView() {
		this.swiperPageRef = 1;
		this.relatedPieces = [];
		this.showSwiper = false;
	}

	// ? API
	getFullPiece$(type: 'electronic' | 'news', file: string): Observable<any> {
		return this.newsService.getFullPiece(type, file).pipe();
	}

	getNews$(): Observable<any> {
		return this.newsService.getNews().pipe();
	}

	getElectronicNews$(): Observable<any> {
		return this.newsService.getElectronicNews().pipe();
	}
}
